import {
  Angle,
  ArrowOption,
  COLOR_VALUES,
  Element,
  ElementType,
  getThickness,
  LineType,
  Pattern,
  PatternMapping,
  Polyline,
  Rect,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { MirrorAxis, mirrorGroup } from '../../helpers/groupHelpers';
import { copy } from '../../helpers/utility';
import { useElementActions } from '../../hooks/useElementActions';
import { InputField, UnitIndicator } from './InputField';
import { useElementUpdate } from '../../hooks/useElementUpdate';
import { useMeasurement } from '../../hooks/useMeasurement';
import { Side, TextAlign } from '../../types';
import { ArrowSelection } from '../ArrowSelection/ArrowSelection';
import { SOLID_FILL_COLORS } from '../Canvas/Patterns';
import { DropdownMenu } from '../ContextMenu/ContextMenu';
import {
  CopyIcon,
  DeleteIcon,
  DimensionLargeIcon,
  DimensionMediumIcon,
  DimensionNoneIcon,
  DimensionSmallIcon,
  DimensionXLargeIcon,
  IconArrowDown,
  IconBricks,
  IconBricksAlternative,
  IconCircle,
  IconCircularDots,
  IconColorFill,
  IconDiagonalHatch,
  IconNoFill,
  IconNoStroke,
  IconRoofTiles,
  IconWoodPanelHorizontal,
  IconWoodPanelVertical,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  MirrorHorizontalIcon,
  MirrorVerticalIcon,
  GroupIcon,
  LayersIcon,
  MoveBottomIcon,
  MoveDownIcon,
  MoveTopIcon,
  MoveUpIcon,
  SolidBorder,
  UngroupIcon,
} from '../Toolbar/SvgIcons';
import {
  DIMENSION_SIZES,
  DimensionSizeConfig,
  FONT_SIZES,
  FontSizeConfig,
  getStrokeIcon,
  PATTERN_SIZES,
  POLYLINE_STROKE_DASH_ARRAYS,
  POLYLINE_THICKNESS_PRESETS_CONFIG,
  PropertyConfig,
  STROKE_DASH_ARRAYS,
  THICKNESS_PRESETS_CONFIG,
  StrokeDashConfig,
} from './controlsConfig';

const PropertyItems = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const PropertyButton = styled.button<{ $isActive: boolean }>`
  background-color: ${(props) =>
    props.$isActive ? 'var(--active-blue)' : 'transparent'};
  border: none;
  padding: 6px;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid
    ${(props) => (props.$isActive ? 'var(--mid-blue)' : 'var(--border-light)')};
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    border: solid 1px
      ${(props) => (props.$isActive ? 'var(--mid-blue)' : 'var(--border)')};
  }
`;

// Labelled variant: the group/ungroup panels have no icon to lean on.
const LabelledPropertyButton = styled(PropertyButton)`
  padding: 6px 12px;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
  gap: 6px;

  svg {
    flex-shrink: 0;
  }
`;

const StrokeDropdownContainer = styled.div<{ $show: boolean }>`
  display: ${(props) => (props.$show ? 'flex' : 'none')};
  flex-direction: column;
  position: absolute;
  right: 0;
  transform: translate(0px, 45px);
  background-color: #fff;
  border-radius: 4px;
  border: solid 1px var(--border);
  padding: 12px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  gap: 24px;
`;

const DropdownSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const ThicknessRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-top: -16px;
  margin-left: -4px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
`;

const ColorButton = styled.div<{ $isActive: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid
    ${(props) =>
      props.$isActive ? 'var(--active-blue)' : 'var(--border-light)'};
  background-color: ${(props) =>
    props.$isActive ? 'var(--hover-blue)' : 'transparent'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--border);
  }
`;

const P = styled.p<{ fontWeight?: string; $isActive?: boolean }>`
  font-weight: ${(props) => props.fontWeight || '400'};
  margin: 4px;
  white-space: nowrap;
  color: ${(props) => (props.$isActive ? '#fff' : '#000')};
`;

interface PropertyGroupProps {
  element: Element;
}

export const FontSizeGroup = ({ element }: PropertyGroupProps) => {
  const { updateElementStyle } = useElementUpdate();
  const intl = useIntl();

  return (
    <PropertyItems id="property-option">
      {FONT_SIZES.map((config: PropertyConfig<FontSizeConfig>) => {
        const isActive = element.style?.fontSize === config.value.fontSize;
        return (
          <PropertyButton
            key={config.value.fontSize}
            id="property-option"
            title={intl.formatMessage({ id: config.title })}
            $isActive={isActive}
            onClick={() => updateElementStyle(element, config.value)}
          >
            <P as="span" fontWeight="500" $isActive={isActive}>
              {config.label}
            </P>
          </PropertyButton>
        );
      })}
    </PropertyItems>
  );
};

export const TextAlignGroup = ({ element }: PropertyGroupProps) => {
  const { updateElementStyle } = useElementUpdate();
  const intl = useIntl();
  const currentAlign = (element.style?.textAlign as string) || TextAlign.Center;

  const options = [
    {
      value: TextAlign.Left,
      title: intl.formatMessage({ id: 'controls.alignLeft' }),
      icon: AlignLeftIcon,
    },
    {
      value: TextAlign.Center,
      title: intl.formatMessage({ id: 'controls.alignCenter' }),
      icon: AlignCenterIcon,
    },
    {
      value: TextAlign.Right,
      title: intl.formatMessage({ id: 'controls.alignRight' }),
      icon: AlignRightIcon,
    },
  ];

  return (
    <PropertyItems id="property-option">
      {options.map(({ value, title, icon: Icon }) => {
        const isActive = currentAlign === value;
        return (
          <PropertyButton
            key={value}
            id="property-option"
            title={title}
            $isActive={isActive}
            onClick={() => updateElementStyle(element, { textAlign: value })}
          >
            <Icon isFill={isActive} />
          </PropertyButton>
        );
      })}
    </PropertyItems>
  );
};

const getDimensionIcon = (fontSize: string | undefined, isFill: boolean) => {
  switch (fontSize) {
    case '10px':
      return <DimensionSmallIcon isFill={isFill} />;
    case '14px':
      return <DimensionMediumIcon isFill={isFill} />;
    case '22px':
      return <DimensionLargeIcon isFill={isFill} />;
    case '40px':
      return <DimensionXLargeIcon isFill={isFill} />;
    default:
      return <DimensionNoneIcon isFill={isFill} />;
  }
};

const getSelectedDimensionIcon = (
  showDimensions: boolean,
  fontSize: string | undefined,
  isOpen: boolean
) => {
  if (!showDimensions) {
    return <DimensionNoneIcon isFill={isOpen} />;
  }
  return getDimensionIcon(fontSize, isOpen);
};

interface DropdownProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const DimensionsDropdown = ({
  element,
  isOpen,
  onToggle,
}: PropertyGroupProps & DropdownProps) => {
  const { updateElement } = useElementUpdate();
  const intl = useIntl();

  const onChangeDimension = (config: DimensionSizeConfig) => () => {
    if (config.showDimensions) {
      updateElement(element, {
        showDimensions: true,
        style: { ...element.style, fontSize: config.fontSize },
      });
    } else {
      updateElement(element, { showDimensions: false });
    }
    onToggle();
  };

  const getIsActive = (config: DimensionSizeConfig) => {
    if (!config.showDimensions) {
      return !element.showDimensions;
    }
    return (
      element.showDimensions && element.style?.fontSize === config.fontSize
    );
  };

  const dimensionOptions = DIMENSION_SIZES.map((config) => ({
    label: intl.formatMessage({ id: config.title }),
    icon: getDimensionIcon(config.fontSize, getIsActive(config)),
    onClick: onChangeDimension(config),
    isActive: getIsActive(config),
  }));

  return (
    <PropertyItems id="property-dimensions">
      <PropertyButton
        id="property-dimensions"
        title={intl.formatMessage({ id: 'controls.dimensionsTitle' })}
        $isActive={isOpen}
        onClick={onToggle}
        style={{ position: 'relative' }}
      >
        {getSelectedDimensionIcon(
          element.showDimensions ?? false,
          element.style?.fontSize as string | undefined,
          isOpen
        )}
        <IconArrowDown isFill={isOpen} />
      </PropertyButton>
      <DropdownMenu show={isOpen} values={dimensionOptions} />
    </PropertyItems>
  );
};

export const MirroredGroup = ({ element }: PropertyGroupProps) => {
  const { updateElement, updateGroup } = useElementUpdate();
  const intl = useIntl();
  if (element.type === ElementType.Group) {
    const mirror = (axis: MirrorAxis) => () =>
      updateGroup(element, (group, children) =>
        mirrorGroup(group, children, axis)
      );
    return (
      <PropertyItems id="property-items">
        <PropertyButton
          id="property-option"
          title={intl.formatMessage({ id: 'controls.mirrorHorizontal' })}
          onClick={mirror('horizontal')}
          $isActive={false}
        >
          <MirrorHorizontalIcon color={'var(--dark-blue)'} isFill={false} />
        </PropertyButton>
        <PropertyButton
          id="property-option"
          title={intl.formatMessage({ id: 'controls.mirrorVertical' })}
          onClick={mirror('vertical')}
          $isActive={false}
        >
          <MirrorVerticalIcon color={'var(--dark-blue)'} isFill={false} />
        </PropertyButton>
      </PropertyItems>
    );
  }
  if (element.type !== ElementType.Fixture) {
    return null;
  }
  return (
    <PropertyItems id="property-items">
      <PropertyButton
        id="property-option"
        title={intl.formatMessage({ id: 'controls.mirrorHorizontal' })}
        onClick={() => {
          updateElement(element, {
            mirroredX: !element.mirroredX,
          });
        }}
        $isActive={element.mirroredX ?? false}
      >
        <MirrorHorizontalIcon
          color={'var(--dark-blue)'}
          isFill={element.mirroredX ?? false}
        />
      </PropertyButton>
      <PropertyButton
        id="property-option"
        title={intl.formatMessage({ id: 'controls.mirrorVertical' })}
        onClick={() => {
          updateElement(element, {
            mirroredY: !element.mirroredY,
          });
        }}
        $isActive={element.mirroredY ?? false}
      >
        <MirrorVerticalIcon
          color={'var(--dark-blue'}
          isFill={element.mirroredY ?? false}
        />
      </PropertyButton>
    </PropertyItems>
  );
};

export const ArrowsGroup = ({ element }: PropertyGroupProps) => {
  const { updateElement } = useElementUpdate();
  const polylineElement = element as Polyline;

  if (polylineElement.lineType !== LineType.Line) {
    return null;
  }

  return (
    <PropertyItems>
      <ArrowSelection
        onSelectArrowOption={(side, arrowOption) => {
          const arrowSide = side === Side.left ? 'leftArrow' : 'rightArrow';
          updateElement(element, {
            [arrowSide]: arrowOption,
          });
        }}
        isLeftActive={polylineElement['leftArrow'] !== ArrowOption.none}
        isRightActive={polylineElement['rightArrow'] !== ArrowOption.none}
      />
    </PropertyItems>
  );
};

const COLOR_FILL_OPTIONS: { pattern: Pattern; labelId: string }[] = [
  { pattern: PatternMapping.BLACK, labelId: 'controls.patternBlack' },
  { pattern: PatternMapping.DARK_GREY, labelId: 'controls.patternDarkGrey' },
  { pattern: PatternMapping.LIGHT_GREY, labelId: 'controls.patternLightGrey' },
  { pattern: PatternMapping.RED, labelId: 'controls.patternRed' },
  { pattern: PatternMapping.ORANGE, labelId: 'controls.patternOrange' },
  { pattern: PatternMapping.YELLOW, labelId: 'controls.patternYellow' },
  { pattern: PatternMapping.GREEN, labelId: 'controls.patternGreen' },
  { pattern: PatternMapping.BLUE, labelId: 'controls.patternBlue' },
];

const TEXTURE_PATTERN_OPTIONS: { pattern: Pattern; labelId: string }[] = [
  { pattern: PatternMapping.NONE, labelId: 'controls.patternNone' },
  { pattern: PatternMapping.WHITE, labelId: 'controls.patternWhiteFill' },
  {
    pattern: PatternMapping.DIAGONAL_HATCH,
    labelId: 'controls.patternDiagonalHatch',
  },
  { pattern: PatternMapping.CIRCULAR_DOTS, labelId: 'controls.patternDots' },
  {
    pattern: PatternMapping.STAGGED_SQUARE_WALL,
    labelId: 'controls.patternBrick1',
  },
  { pattern: PatternMapping.BRICK_WALL, labelId: 'controls.patternBrick2' },
  { pattern: PatternMapping.ROOF_TILES, labelId: 'controls.patternRoofTiles' },
  {
    pattern: PatternMapping.WOOD_PANEL,
    labelId: 'controls.patternVerticalWood',
  },
  {
    pattern: PatternMapping.WOOD_PANEL_HORIZONTAL,
    labelId: 'controls.patternHorizontalWood',
  },
];

const getPatternIcon = (
  pattern: Pattern,
  isFill: boolean,
  activeColor?: string
) => {
  switch (pattern) {
    case PatternMapping.WHITE:
      return (
        <SolidBorder
          color={'var(--dark-blue)'}
          isFill={isFill}
          activeColor={activeColor}
        />
      );
    case PatternMapping.DIAGONAL_HATCH:
      return <IconDiagonalHatch isFill={isFill} activeColor={activeColor} />;
    case PatternMapping.WOOD_PANEL:
      return (
        <IconWoodPanelVertical isFill={isFill} activeColor={activeColor} />
      );
    case PatternMapping.WOOD_PANEL_HORIZONTAL:
      return (
        <IconWoodPanelHorizontal isFill={isFill} activeColor={activeColor} />
      );
    case PatternMapping.STAGGED_SQUARE_WALL:
      return <IconBricks isFill={isFill} activeColor={activeColor} />;
    case PatternMapping.BRICK_WALL:
      return (
        <IconBricksAlternative isFill={isFill} activeColor={activeColor} />
      );
    case PatternMapping.ROOF_TILES:
      return <IconRoofTiles isFill={isFill} activeColor={activeColor} />;
    case PatternMapping.CIRCULAR_DOTS:
      return <IconCircularDots isFill={isFill} activeColor={activeColor} />;
    default: {
      const solid = SOLID_FILL_COLORS[pattern];
      if (solid) {
        return (
          <IconColorFill
            fillColor={solid}
            isFill={isFill}
            activeColor={activeColor}
          />
        );
      }
      return <IconNoFill isFill={isFill} activeColor={activeColor} />;
    }
  }
};

export const PatternDropdown = ({
  element,
  isOpen,
  onToggle,
}: PropertyGroupProps & DropdownProps) => {
  const { updateElement } = useElementUpdate();
  const intl = useIntl();

  const currentPattern = element?.pattern || PatternMapping.NONE;
  const isTexturePattern =
    currentPattern !== PatternMapping.NONE &&
    !SOLID_FILL_COLORS[currentPattern];
  const currentPatternSize = element?.patternSize ?? 1;

  const onChangeElementPattern = (pattern: Pattern) => () => {
    const newPattern =
      element.pattern === pattern ? PatternMapping.NONE : pattern;
    updateElement(element, { pattern: newPattern });
  };

  const onChangePatternSize = (patternSize: number) => {
    updateElement(element, { patternSize });
  };

  const selectedPatternIcon = getPatternIcon(currentPattern, isOpen);

  return (
    <PropertyItems id="property-symbols">
      <PropertyButton
        id="property-more"
        title={intl.formatMessage({ id: 'controls.patternTitle' })}
        $isActive={isOpen}
        onClick={onToggle}
        style={{ position: 'relative' }}
      >
        {selectedPatternIcon}
        <IconArrowDown isFill={isOpen} />
      </PropertyButton>
      <StrokeDropdownContainer $show={isOpen}>
        {isTexturePattern && (
          <DropdownSection>
            {PATTERN_SIZES.map((config: PropertyConfig<number>) => {
              const isActive = currentPatternSize === config.value;
              return (
                <PropertyButton
                  key={config.value}
                  title={intl.formatMessage({ id: config.title })}
                  $isActive={isActive}
                  onClick={() => onChangePatternSize(config.value)}
                >
                  <P as="span" fontWeight="500" $isActive={isActive}>
                    {config.label}
                  </P>
                </PropertyButton>
              );
            })}
          </DropdownSection>
        )}
        <ColorGrid>
          {TEXTURE_PATTERN_OPTIONS.map(({ pattern, labelId }) => {
            const isActive = currentPattern === pattern;
            return (
              <ColorButton
                key={pattern}
                $isActive={isActive}
                title={intl.formatMessage({ id: labelId })}
                onClick={onChangeElementPattern(pattern)}
              >
                {getPatternIcon(pattern, isActive, 'var(--text)')}
              </ColorButton>
            );
          })}
        </ColorGrid>
        <ColorGrid>
          {COLOR_FILL_OPTIONS.map(({ pattern, labelId }) => {
            const isActive = currentPattern === pattern;
            return (
              <ColorButton
                key={pattern}
                $isActive={isActive}
                title={intl.formatMessage({ id: labelId })}
                onClick={onChangeElementPattern(pattern)}
              >
                <IconColorFill
                  fillColor={SOLID_FILL_COLORS[pattern] ?? '#fff'}
                  isFill={isActive}
                  activeColor="var(--text)"
                />
              </ColorButton>
            );
          })}
        </ColorGrid>
      </StrokeDropdownContainer>
    </PropertyItems>
  );
};

export const TextColorDropdown = ({
  element,
  isOpen,
  onToggle,
}: PropertyGroupProps & DropdownProps) => {
  const { updateElementStyle } = useElementUpdate();
  const intl = useIntl();

  const onChangeTextColor = (color: string) => {
    updateElementStyle(element, { color });
    onToggle();
  };

  const currentColor = element?.style?.color || '#000';
  const selectedColorIcon = <IconCircle color={currentColor} />;

  return (
    <PropertyItems id="property-text-color">
      <PropertyButton
        id="property-text-color"
        title={intl.formatMessage({ id: 'controls.textColorTitle' })}
        $isActive={isOpen}
        onClick={onToggle}
        style={{ position: 'relative' }}
      >
        {selectedColorIcon}
        <IconArrowDown isFill={isOpen} />
      </PropertyButton>
      <StrokeDropdownContainer $show={isOpen}>
        <ColorGrid>
          {COLOR_VALUES.filter((color) => color !== 'none').map((color) => (
            <ColorButton
              key={color}
              $isActive={currentColor === color}
              title={color}
              onClick={() => onChangeTextColor(color)}
            >
              <IconCircle color={color} />
            </ColorButton>
          ))}
        </ColorGrid>
      </StrokeDropdownContainer>
    </PropertyItems>
  );
};

export const StrokeColorDropdown = ({
  element,
  isOpen,
  onToggle,
}: PropertyGroupProps & DropdownProps) => {
  const { updateElementStyle } = useElementUpdate();
  const intl = useIntl();

  const onChangeStrokeColor = (stroke: string) => {
    updateElementStyle(element, { stroke });
    onToggle();
  };

  const currentStroke = element?.style?.stroke || '#000';
  const selectedStrokeIcon =
    currentStroke === 'none' ? (
      <IconNoStroke />
    ) : (
      <IconCircle color={currentStroke} />
    );

  return (
    <PropertyItems id="property-stroke-color">
      <PropertyButton
        id="property-stroke-color"
        title={intl.formatMessage({ id: 'controls.strokeColorTitle' })}
        $isActive={isOpen}
        onClick={onToggle}
        style={{ position: 'relative' }}
      >
        {selectedStrokeIcon}
        <IconArrowDown isFill={isOpen} />
      </PropertyButton>
      <StrokeDropdownContainer $show={isOpen}>
        <ColorGrid>
          {COLOR_VALUES.map((color) => (
            <ColorButton
              key={color}
              $isActive={currentStroke === color}
              title={
                color === 'none'
                  ? intl.formatMessage({ id: 'controls.strokeColorNone' })
                  : color
              }
              onClick={() => onChangeStrokeColor(color)}
            >
              {color === 'none' ? (
                <IconNoStroke />
              ) : (
                <IconCircle color={color} />
              )}
            </ColorButton>
          ))}
        </ColorGrid>
      </StrokeDropdownContainer>
    </PropertyItems>
  );
};

export const StrokeDropdown = ({
  element,
  isOpen,
  onToggle,
}: PropertyGroupProps & DropdownProps) => {
  const { updateElement, updateElementStyle } = useElementUpdate();
  const intl = useIntl();
  const { unitSystem, lengthFormat, unitLabel } = useMeasurement();
  const thicknessTitleKey =
    unitSystem === 'imperial'
      ? 'controls.thicknessExactImperial'
      : lengthFormat === 'millimeters'
        ? 'controls.thicknessExactMillimeters'
        : 'controls.thicknessExact';

  const isPolyline = element.type === ElementType.Polyline;
  const thicknessPresets = isPolyline
    ? POLYLINE_THICKNESS_PRESETS_CONFIG
    : THICKNESS_PRESETS_CONFIG;
  const hasMeasurableThickness =
    element.type === ElementType.Rect ||
    element.type === ElementType.Triangle ||
    element.type === ElementType.Trapezoid ||
    element.type === ElementType.Angle ||
    element.type === ElementType.Polyline;
  const dashConfigs = isPolyline
    ? POLYLINE_STROKE_DASH_ARRAYS
    : STROKE_DASH_ARRAYS;

  const onChangeStrokeColor = (stroke: string) => {
    updateElementStyle(element, { stroke });
  };

  const onChangeThickness = (thickness: number) => {
    updateElement(element, { ...copy(element), thickness });
  };

  const [exactThicknessToggle, setExactThicknessToggle] = useState<{
    elementId: string;
    show: boolean;
  } | null>(null);

  const isCustomThickness = !thicknessPresets.some(
    (config) => config.value === getThickness(element)
  );
  const toggledThickness =
    exactThicknessToggle?.elementId === element.id
      ? exactThicknessToggle.show
      : null;
  const showThicknessInput =
    hasMeasurableThickness && (toggledThickness ?? isCustomThickness);

  const onChangeStrokeDash = (config: StrokeDashConfig) => {
    if (config.additionalProps) {
      const el = copy(element);
      const newElement = {
        ...el,
        ...config.additionalProps,
        style: {
          ...el.style,
          strokeDasharray: config.value,
        },
      };
      updateElement(element, newElement);
    } else {
      updateElementStyle(element, { strokeDasharray: config.value });
    }
  };

  const isDashActive = (config: StrokeDashConfig) => {
    let isActive = element.style?.strokeDasharray === config.value;

    if (!isPolyline && config.value === undefined) {
      isActive = element.style?.strokeDasharray === undefined;
    }

    const additionalPropsMatch = config.additionalProps
      ? Object.entries(config.additionalProps).every(
          ([key, value]) =>
            ((element as unknown as Record<string, unknown>)[key] ?? false) ===
            value
        )
      : true;

    return isActive && additionalPropsMatch;
  };

  const currentStroke = element?.style?.stroke;
  const selectedStrokeIcon =
    currentStroke === 'none' ? (
      <IconNoStroke />
    ) : (
      <IconCircle color={currentStroke || '#000'} />
    );

  return (
    <PropertyItems id="property-stroke">
      <PropertyButton
        id="property-stroke"
        title={intl.formatMessage({ id: 'controls.strokeTitle' })}
        $isActive={isOpen}
        onClick={onToggle}
        style={{ position: 'relative' }}
      >
        {selectedStrokeIcon}
        <IconArrowDown isFill={isOpen} />
      </PropertyButton>
      <StrokeDropdownContainer $show={isOpen}>
        <DropdownSection>
          {thicknessPresets.map((config: PropertyConfig<number>) => {
            const isActive = getThickness(element) === config.value;
            return (
              <PropertyButton
                key={config.value}
                title={intl.formatMessage({ id: config.title })}
                $isActive={isActive}
                onClick={() => onChangeThickness(config.value)}
              >
                <P as="span" fontWeight="500" $isActive={isActive}>
                  {config.label}
                </P>
              </PropertyButton>
            );
          })}
          {hasMeasurableThickness && (
            <PropertyButton
              title={intl.formatMessage({ id: thicknessTitleKey })}
              $isActive={showThicknessInput}
              onClick={() =>
                setExactThicknessToggle({
                  elementId: element.id,
                  show: !showThicknessInput,
                })
              }
            >
              <P as="span" fontWeight="500" $isActive={showThicknessInput}>
                …
              </P>
            </PropertyButton>
          )}
        </DropdownSection>
        {showThicknessInput && (
          <ThicknessRow title={intl.formatMessage({ id: thicknessTitleKey })}>
            <InputField
              key={`${element.id}-thickness`}
              element={
                element as Rect | Triangle | Trapezoid | Angle | Polyline
              }
              property="thickness"
            />
            {unitLabel && <UnitIndicator>{unitLabel}</UnitIndicator>}
          </ThicknessRow>
        )}
        <DropdownSection>
          {dashConfigs.map((config: StrokeDashConfig, index: number) => {
            const isActive = isDashActive(config);
            return (
              <PropertyButton
                key={`${config.value}-${index}`}
                title={intl.formatMessage({ id: config.title })}
                $isActive={isActive}
                onClick={() => onChangeStrokeDash(config)}
              >
                {getStrokeIcon(config.icon, isActive)}
              </PropertyButton>
            );
          })}
        </DropdownSection>
        <ColorGrid>
          {COLOR_VALUES.map((color) => (
            <ColorButton
              key={color}
              $isActive={currentStroke === color}
              title={
                color === 'none'
                  ? intl.formatMessage({ id: 'controls.strokeColorNone' })
                  : color
              }
              onClick={() => onChangeStrokeColor(color)}
            >
              {color === 'none' ? (
                <IconNoStroke />
              ) : (
                <IconCircle color={color} />
              )}
            </ColorButton>
          ))}
        </ColorGrid>
      </StrokeDropdownContainer>
    </PropertyItems>
  );
};

export const DeleteButton = () => {
  const { onDelete } = useElementActions();
  const intl = useIntl();

  return (
    <PropertyButton
      $isActive={false}
      title={intl.formatMessage({ id: 'controls.deleteButton' })}
      onClick={onDelete}
    >
      <DeleteIcon />
    </PropertyButton>
  );
};

export const CopyButton = () => {
  const { onDuplicate } = useElementActions();
  const intl = useIntl();

  return (
    <PropertyButton
      $isActive={false}
      title={intl.formatMessage({ id: 'controls.copyButton' })}
      onClick={onDuplicate}
    >
      <CopyIcon />
    </PropertyButton>
  );
};

export const GroupButton = () => {
  const { onGroup } = useElementActions();
  const intl = useIntl();

  return (
    <LabelledPropertyButton
      $isActive={false}
      title={intl.formatMessage({ id: 'controls.groupButtonTitle' })}
      onClick={onGroup}
    >
      <GroupIcon />
      {intl.formatMessage({ id: 'controls.groupButton' })}
    </LabelledPropertyButton>
  );
};

export const UngroupButton = () => {
  const { onUngroup } = useElementActions();
  const intl = useIntl();

  return (
    <LabelledPropertyButton
      $isActive={false}
      title={intl.formatMessage({ id: 'controls.ungroupButtonTitle' })}
      onClick={onUngroup}
    >
      <UngroupIcon />
      {intl.formatMessage({ id: 'controls.ungroupButton' })}
    </LabelledPropertyButton>
  );
};

export const MoreDropdown = ({ isOpen, onToggle }: DropdownProps) => {
  const { onBringDown, onBringToBottom, onBringToTop, onBringUp } =
    useElementActions();
  const intl = useIntl();

  const onClickItem = (callback: VoidFunction) => () => {
    callback();
    onToggle();
  };

  const elementActions = [
    {
      label: intl.formatMessage({ id: 'controls.moveToTop' }),
      icon: <MoveTopIcon />,
      onClick: onClickItem(onBringToTop),
    },
    {
      label: intl.formatMessage({ id: 'controls.moveUp' }),
      icon: <MoveUpIcon />,
      onClick: onClickItem(onBringUp),
    },
    {
      label: intl.formatMessage({ id: 'controls.moveDown' }),
      icon: <MoveDownIcon />,
      onClick: onClickItem(onBringDown),
    },
    {
      label: intl.formatMessage({ id: 'controls.moveToBottom' }),
      icon: <MoveBottomIcon />,
      onClick: onClickItem(onBringToBottom),
    },
  ];

  return (
    <PropertyItems id="property-more">
      <PropertyButton
        id="property-more"
        title={intl.formatMessage({ id: 'controls.reorderTitle' })}
        $isActive={isOpen}
        onClick={onToggle}
        style={{ position: 'relative' }}
      >
        <LayersIcon isFill={isOpen} />
        <IconArrowDown isFill={isOpen} />
      </PropertyButton>
      <DropdownMenu show={isOpen} values={elementActions} />
    </PropertyItems>
  );
};
